
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
        errorInfo: null,
    };

    public static getDerivedStateFromError(error: Error): State {
        // Update state so the next render will show the fallback UI.
        return { hasError: true, error, errorInfo: null };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("Uncaught error:", error, errorInfo);
        this.setState({ error, errorInfo });
        // TODO: Send to your custom analytics service here
        // trackEvent('error', { message: error.message, stack: errorInfo.componentStack });
    }

    private handleReload = () => {
        window.location.reload();
    };

    private handleGoHome = () => {
        window.location.href = '/';
    };

    public render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 font-poppins">
                    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center border border-gray-100">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-50 mb-6">
                            <AlertTriangle className="w-8 h-8 text-red-500" />
                        </div>

                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h1>
                        <p className="text-gray-500 mb-8">
                            We encountered an unexpected error. Please try reloading the page.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
                            <button
                                onClick={this.handleReload}
                                className="flex items-center justify-center gap-2 px-6 py-2.5 bg-[#B08D55] hover:bg-[#a08258] text-white rounded-lg transition-colors font-medium shadow-md shadow-[#B08D55]/20"
                            >
                                <RefreshCw className="w-4 h-4" />
                                Reload Page
                            </button>

                            <button
                                onClick={this.handleGoHome}
                                className="flex items-center justify-center gap-2 px-6 py-2.5 bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 rounded-lg transition-colors font-medium"
                            >
                                <Home className="w-4 h-4" />
                                Go Home
                            </button>
                        </div>

                        {/* Developer Details (Auto-hidden in production usually, but good for now) */}
                        {this.state.error && (
                            <div className="text-left mt-6 pt-6 border-t border-gray-100">
                                <details className="cursor-pointer group">
                                    <summary className="text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors list-none flex items-center gap-1">
                                        <span className="group-open:rotate-90 transition-transform">▶</span> Show Error Details
                                    </summary>
                                    <div className="mt-3 p-3 bg-gray-900 rounded-lg overflow-x-auto">
                                        <p className="text-red-400 text-xs font-mono font-bold mb-2">
                                            {this.state.error.toString()}
                                        </p>
                                        <pre className="text-gray-400 text-[10px] leading-relaxed font-mono whitespace-pre-wrap">
                                            {this.state.errorInfo?.componentStack}
                                        </pre>
                                    </div>
                                </details>
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
